import BaseComponent from "./base_component_controller";

import {
  ensureResultsMonotonic,
  nestedGroupByToMap,
  foldedMap,
  extractCascade,
} from "hb/lib/collection_helpers";

import { useResize } from "hb/insights/use_resize";
import { useChartConfig } from "hb/insights/use_chart_config";
import { dimensionType, axisType, valueFormatter } from "hb/insights/chart";
import { isDatetimeType, isNumberType } from "hb/insights/types";

export default class extends BaseComponent {
  static viewId = "line";
  static get values() {
    return {
      ...BaseComponent.values,

      querySessionId: String,
      schema: Array,
      results: Array,
      chartConfig: Object,
    };
  }

  static configs = [
    "xField",
    "yField",
    "zField",
    "colorField",

    { name: "groupType", default: "events" },
    { name: "groups", default: "{}", type: "json" },

    "xFieldUnit",
    "yFieldUnit",

    // Default left axis
    "yAxisLabel",
    "yAxisMin",
    "yAxisMax",

    // Optional right Axis
    "rightYAxisFormat",
    "rightYAxisLabel",
    "rightYAxisMin",
    "rightYAxisMax",
  ];

  connect() {
    super.connect();

    useChartConfig(this);
  }

  outletsConnected() {
    super.outletsConnected();

    useResize(this, { element: this.outletComponent("chart").chartTarget });
    this.initQuery();
  }

  queryContextDefaults() {
    const { meta } = this.query;
    let config = {};
    const fieldTypes = Object.entries(meta.fieldTypes);

    const xRes = extractCascade(
      fieldTypes,
      ([, t]) => isDatetimeType(t),
      ([, t]) => isNumberType(t),
    );

    const yRes = extractCascade(
      xRes[1],
      ([, t]) => isNumberType(t),
      ([, t]) => isDatetimeType(t),
    );

    const zRes = extractCascade(yRes[1], ([, t]) => t === "string");

    if (xRes[0] !== undefined) config.xField = xRes[0][0];
    if (yRes[0] !== undefined) config.yField = yRes[0][0];
    if (zRes[0] !== undefined) config.zField = zRes[0][0];

    return config;
  }

  validate() {
    let { xField, yField, zField, groupType } = this.config;

    if (!xField || (!yField && groupType === "events")) {
      this.errorMessage =
        "Select fields in chart config or update data to fit chart";
    } else if (this.hasUnknownFields(xField, yField, zField)) {
      this.errorMessage = "Unknown field selected";
    }
  }

  get shouldSkip() {
    return !this.config.xField || !this.query.results;
  }

  processConfig() {
    if (this.shouldSkip) return;

    let { groupType, groups, xField, yField, zField, colorField } = this.config;
    let { results, meta } = this.query;

    // Build up color fields
    //
    let colorFieldMap = {};
    if (colorField !== "") {
      colorFieldMap = results.reduce((acc, d) => {
        const key = zField !== "" ? d.field(zField) : yField;
        const colorVal = d.field(colorField);
        acc[key] = this.colorMapValue[colorVal] || colorVal;
        return acc;
      }, {});

      this.derivedConfig.colorFieldMap = colorFieldMap;
    }

    // Field group config
    //
    // Field group items can be configured on a per item basic in the "Fields"
    // section of the config. Here users can
    //
    // * Hide a group from the chart
    // * Change the color of a group
    // * Determine the orientation of the group (left or right)
    //
    let groupConfig;

    // Cycle through colors, but start over on each render to ensure all
    // groups get the same colors. We iterate an index even if a custom color
    // has been applied to keep the colorIdx in sync from the previous change.
    let colorIdx = -1;
    const gatherColor = (n) => {
      colorIdx = (colorIdx + 1) % this.colorValues.length;
      return groups[n]?.color || colorFieldMap[n] || this.colorValues[colorIdx];
    };

    if (groupType === "fields") {
      const colNames = meta.fields.filter((n) => n != xField);
      groupConfig = colNames.map((name, idx) => ({
        idx,
        name,
        disabled: this.groupDisabled(idx),
        color: gatherColor(name),
        axis: groups[name]?.axis || "left",
      }));
    } else if (zField) {
      const zDomain = Array.from(new Set(results.map((d) => d.field(zField))));
      this.derivedConfig.zDomain = zDomain;
      groupConfig = zDomain.map((name, idx) => ({
        idx,
        name,
        disabled: this.groupDisabled(idx),
        color: gatherColor(name),
        axis: groups[name]?.axis || "left",
      }));
    } else {
      groupConfig = [
        {
          idx: 0,
          name: yField,
          color: gatherColor(yField),
          axis: "left",
        },
      ];
    }

    this.derivedConfig.groupConfig = groupConfig;
  }

  renderConfig() {
    const { meta } = this.query;

    ["xField", "yField", "zField", "colorField"].forEach((name) => {
      let allFields;
      if (name === "zField" || name === "colorField") {
        allFields = ["__empty", ...meta.fields];
      } else {
        allFields = meta.fields;
      }

      this.find(`[data-input-name="${name}"] ul`).innerHTML = allFields
        .map((field) =>
          this.comboboxItemTemplate({
            classes: this[name] === field ? "current" : "",
            value: field,
            id: field,
            display: field,
            ...(field == "__empty" && {
              display: null,
              badge: "none",
            }),
          }),
        )
        .join("\n");
    });

    this.find(
      `input[name="groupType"][id="${this.config.groupType}"]`,
    ).checked = "true";
    this.find(`input[name="xField"]`).value = this.config.xField || "";
    this.find(`input[name="yField"]`).value = this.config.yField || "";
    this.find(`input[name="zField"]`).value = this.config.zField || "";
    this.find(`input[name="colorField"]`).value = this.config.colorField || "";

    this.renderFieldGroups(this.derivedConfig.groupConfig);
  }

  render() {
    if (this.shouldSkip) return;

    let { meta, results } = this.query;
    let {
      xField,
      yField,
      zField,
      xFieldUnit,
      yFieldUnit,
      xAxisLabel,
      groups,
      groupType,
    } = this.config;

    const xFieldType = meta.type(xField);

    const lineSeriesDefault = {
      type: "line",
      animation: false,
      lineStyle: { width: 1.5 },
      showSymbol: false,
      emphasis: {
        scale: true,
        focus: "series",
      },
      blur: {
        lineStyle: { opacity: 0.4 },
      },
    };

    results = ensureResultsMonotonic(results, xField);

    let zDomain, series, dataset;
    let xAxis, yAxis;

    //
    // Dataset with grouping for all other fields
    //
    if (groupType === "fields") {
      const colNames = meta.fields.filter((n) => n != xField);

      dataset = {
        dimensions: [
          { name: xField, type: dimensionType(xFieldType) },
          ...colNames,
        ],
        source: results.map((r) => r.slice(xField, ...colNames)),
      };

      if (colNames.some((z) => groups[z]?.axis === "right")) {
        yAxis = [
          {
            ...this.defaultYAxis,
            alignTicks: true,
            name: "",
          },
          {
            ...this.defaultYAxis,
            name: "",
            alignTicks: true,
            nameTextStyle: { align: "right" },
          },
        ];
      } else {
        yAxis = { ...this.defaultYAxis, name: "" };
      }

      series = this.derivedConfig.groupConfig
        .filter(({ disabled }) => !disabled)
        .map(({ name, color, axis }) => ({
          ...lineSeriesDefault,
          yAxisIndex: axis === "right" ? 1 : undefined,
          name,
          itemStyle: { color },
          dimensions: [xField, name],
        }));

      //
      // Field grouping (zField)
      //
    } else if (zField) {
      zDomain = Array.from(new Set(results.map((d) => d.field(zField))));

      const nested = nestedGroupByToMap(
        results,
        (r) => [r.field(xField), r.field(zField)],
        (r) => r.field(yField),
      );

      const folded = foldedMap(nested, xField);
      dataset = {
        source: folded,
        dimensions: [
          { name: xField, type: dimensionType(xFieldType) },
          ...zDomain,
        ],
      };

      if (zDomain.some((z) => groups[z]?.axis === "right")) {
        yAxis = [
          {
            ...this.defaultYAxis,
            alignTicks: true,
            name: "",
          },
          {
            name: "",
            alignTicks: true,
            nameTextStyle: { align: "right" },
          },
        ];
      } else {
        yAxis = { ...this.defaultYAxis, name: "" };
      }

      series = this.derivedConfig.groupConfig
        .filter(({ disabled }) => !disabled)
        .map(({ name, color, axis }) => ({
          ...lineSeriesDefault,
          yAxisIndex: axis === "right" ? 1 : undefined,
          name,
          itemStyle: { color },
          dimensions: [xField, name],
        }));

      //
      // Dataset with simple x and y
      //
    } else {
      dataset = {
        dimensions: [xField, yField],
        source: results.map((r) => r.slice(yField, xField)),
      };

      series = [
        {
          ...lineSeriesDefault,
          itemStyle: { color: this.derivedConfig.groupConfig[0].color },
          name: yField,
        },
      ];
      yAxis = { ...this.defaultYAxis, name: "" };
    }

    xAxis = {
      type: axisType(xFieldType),
      boundaryGap: false,
      nameLocation: "center",
      // Keeps named axis, but pushes it outside the viewing area
      nameGap: 30,
      name: xAxisLabel || xField,
      min: "dataMin",
      max: "dataMax",
    };

    if (isDatetimeType(xFieldType)) {
      if (meta.queryStartAt) {
        xAxis.min = meta.queryStartAt;
      }

      if (meta.queryEndAt) {
        xAxis.max = meta.queryEndAt;
      }
    }

    let tooltip = {
      trigger: "axis",
    };

    const xFieldFormatter = valueFormatter(xFieldUnit);
    if (xFieldFormatter) {
      xAxis.axisLabel = { formatter: xFieldFormatter };
    }

    const yFieldFormatter = valueFormatter(yFieldUnit);
    if (yFieldFormatter) {
      yAxis.axisLabel = { formatter: yFieldFormatter };
      tooltip.valueFormatter = yFieldFormatter;
    }

    if (xFieldUnit === "percent") {
      xAxis.min = 0;
      xAxis.max = 100;
    }

    if (yFieldUnit === "percent") {
      yAxis.min = 0;
      yAxis.max = 100;
    }

    let defaults = this.defaultChartConfig;

    const options = {
      ...defaults,
      dataset,
      series,
      yAxis,
      xAxis: { ...defaults.xAxis, ...xAxis },
      legend: null,
      tooltip: { ...defaults.tooltip, ...tooltip },
    };

    this.outletComponent("chart").setChartOptions(options);
    this.outletComponent("legend").render(this.derivedConfig.groupConfig);
  }
}
